import { getTablePermissions, hasPermission } from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";
import { MarketOption } from "./projectTableConfig";
import { router } from "expo-router";

const STATUS_OPTIONS = [
  { value: "DRAFT", color: "todo" },
  { value: "SENT", color: "pending" },
  { value: "PAID", color: "complete" },
  { value: "OVERDUE", color: "attention" },
  { value: "CANCELLED", color: "neutral" },
] as const;

const BILLING_STATUS_OPTIONS = [
  { value: "PENDING", color: "neutral" },
  { value: "READY FOR BILLING", color: "todo" },
  { value: "BILLING IN PROGRESS", color: "pending" },
  { value: "PNL PENDING", color: "complete" },
] as const;

const PROJECT_STATUS_OPTIONS = [
  { value: "POTENTIAL", color: "todo" },
  { value: "PENDING APPROVAL", color: "pending" },
  { value: "IN PROGRESS", color: "pending" },
  { value: "ON HOLD", color: "attention" },
  { value: "COMPLETED", color: "complete" },
  { value: "CONFLICTING", color: "attention" },
  { value: "LOST", color: "neutral" },
  { value: "CANCELED", color: "attention" },
] as const;

export const getInvoiceTableConfig = (
  userRoles: UserRoleType[] | null,
  marketOptions: MarketOption[] = [],
  displayNames,
) => {
  const permissions = getTablePermissions(userRoles, "invoices");

  // Build readonly array conditionally based on permissions
  const readonlyColumns: (string | [string, (key: string, value: any, row: any) => void])[] = [
    "id",
    "invoice_number",
    "invoice_date",
    "expiry_date",
    "amount_due",
    "created_at",
    "project_status",
    "project_billing_status",
    "project_market",
    "line_items",
  ];

  // Only add clickable client column if user has clients.read
  if (hasPermission(userRoles, "clients", "read")) {
    readonlyColumns.push([
      "client",
      (_key, _value, row) => row.client && router.setParams({ panel: "client", id: row.client.id }),
    ]);
  }

  // Only add clickable project column if user has projects.read
  if (hasPermission(userRoles, "projects", "read")) {
    readonlyColumns.push([
      "project",
      (_key, _value, row) => row.project && router.setParams({ panel: "project", id: row.project.id }),
    ]);
  }

  return {
    entityName: "Invoice",
    entityNamePlural: "Invoices",

    tableConfig: {
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return `${displayNames?.invoices?.name ?? "Invoice"} Id`;
          case "client":
            return displayNames?.clients?.name ?? "Client";
          case "project":
            return displayNames?.projects?.name ?? "Project";
          case "project_status":
            return `${displayNames?.projects?.name ?? "Project"} Status`;
          case "project_billing_status":
            return `${displayNames?.projects?.name ?? "Project"} Billing Status`;
          case "project_market":
            return `${displayNames?.projects?.name ?? "Project"} Market`;
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      ignore: ["tenant_id", "comments", "client_id", "project_id", "project_data", "meta_data"],
      readonly: readonlyColumns,
      validators: {},
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "line_items") return Array.isArray(value) ? `${value.length} items` : "0 items";
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      dataTypes: {
        created_at: "date" as const,
        amount_due: "currency" as const,
        invoice_date: "date" as const,
        expiry_date: "date" as const,
        client: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        project: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        status: {
          type: "enum" as const,
          options: STATUS_OPTIONS,
        },
        project_status: {
          type: "enum" as const,
          options: PROJECT_STATUS_OPTIONS,
        },
        project_billing_status: {
          type: "enum" as const,
          options: BILLING_STATUS_OPTIONS,
        },
        ...(hasPermission(userRoles, "markets", "read") && {
          project_market: {
            type: "array" as const,
            picker: "market" as const,
            chipColor: "#4169E1",
            options: marketOptions,
          },
        }),
      },
    },

    filterChoices: [
      {
        name: "id",
        kind: "text" as const,
        displayName: `${displayNames?.invoices?.name ?? "Invoice"} Id`,
        menus: ["filter", "visible"] as const,
      },
      { name: "invoice_number", kind: "text" as const, menus: ["filter", "visible"] as const },
      {
        name: "status",
        kind: "enum" as const,
        options: [...STATUS_OPTIONS],
        menus: ["filter", "visible", "groupby", "orderby"] as const,
      },
      {
        name: "client",
        displayName: `${displayNames?.clients?.name ?? "Client"}`,
        kind: "linked_record" as const,
        menus: ["filter", "visible", "groupby", "orderby"] as const,
        linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
      },
      {
        name: "project",
        displayName: displayNames?.projects?.name ?? "Project",
        kind: "linked_record" as const,
        menus: ["filter", "visible", "groupby", "orderby"] as const,
        linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
      },
      {
        name: "project_status",
        displayName: `${displayNames?.projects?.name ?? "Project"} Status`,
        kind: "enum" as const,
        options: [...PROJECT_STATUS_OPTIONS],
        menus: ["filter", "visible", "groupby", "orderby"] as const,
      },
      {
        name: "project_billing_status",
        displayName: `${displayNames?.projects?.name ?? "Project"} Billing Status`,
        kind: "enum" as const,
        options: [...BILLING_STATUS_OPTIONS],
        menus: ["filter", "visible", "groupby", "orderby"] as const,
      },
      {
        name: "project_market",
        kind: "array" as const,
        displayName: `${displayNames?.projects?.name ?? "Project"} Market`,
        options: marketOptions,
        menus: ["filter", "visible", "groupby"] as const,
      },
      { name: "invoice_date", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
      { name: "expiry_date", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
      { name: "purchase_order_number", kind: "text" as const, menus: ["filter", "visible"] as const },
      { name: "amount_due", kind: "number" as const, menus: ["filter", "visible"] as const },
      { name: "line_items", kind: "text" as const, menus: ["visible"] as const },
      { name: "created_at", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
    ],

    ...permissions,
    storageKey: "invoice_flyout",
  };
};
